import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Icon from '../AppIcon';

const API_URL = 'https://api.autocasses.com/api/produit/all';

const RecentListingsGrid = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [loadingImages, setLoadingImages] = useState({});
  const [displayCount, setDisplayCount] = useState(16); // Multiple de 4 pour une grille propre
  const [recentListings, setRecentListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('recent');
  const [filterCondition, setFilterCondition] = useState('all');

  useEffect(() => {
    fetchRecentListings();
  }, []);

  const getDefaultAvatar = (name) => {
    const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
    const nameToUse = name || t('recentListings.seller');
    const colorIndex = nameToUse.charCodeAt(0) % colors.length;
    
    const initials = nameToUse
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
    
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=${colors[colorIndex].replace('#', '')}&color=fff&size=128`;
  };

  const getSellerAvatar = (seller) => {
    const sellerName = seller.nom || `${t('recentListings.seller')} ${seller.id}`;
    const defaultAvatar = getDefaultAvatar(sellerName);
    
    if (!seller.avatar || seller.avatar === 'picture') {
      return defaultAvatar;
    }
    
    if (seller.avatar.startsWith('http')) {
      return seller.avatar;
    }
    
    return `https://api.autocasses.com/uploads/avatars/${seller.avatar}`;
  };

  const fetchRecentListings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(API_URL, {
        timeout: 30000,
      });
      
      const data = response.data;
      
      if (data.success && Array.isArray(data.data)) {
        const transformedListings = data.data.map((produit) => {
          const createdAt = new Date(produit.createdAt);
          const now = new Date();
          const diffHours = Math.floor((now - createdAt) / (1000 * 60 * 60));
          const diffMinutes = Math.floor((now - createdAt) / (1000 * 60));
          
          let timeAgo;
          if (diffMinutes < 1) {
            timeAgo = t('recentListings.justNow');
          } else if (diffMinutes < 60) {
            timeAgo = `${diffMinutes}${t('recentListings.minutesAgo')}`;
          } else if (diffHours < 24) {
            timeAgo = `${diffHours}${t('recentListings.hoursAgo')}`;
          } else {
            const diffDays = Math.floor(diffHours / 24);
            timeAgo = `${diffDays}${t('recentListings.daysAgo')}`;
          }
          
          const getCondition = (etat) => {
            const etatLower = etat ? etat.toLowerCase() : '';
            
            switch(etatLower) {
              case 'new':
              case 'neuf': return t('recentListings.conditions.new');
              case 'like_new':
              case 'comme neuf': return t('recentListings.conditions.likeNew');
              case 'used':
              case 'occasion': return t('recentListings.conditions.used');
              case 'bon etat': return t('recentListings.conditions.goodCondition');
              case 'excellent etat': return t('recentListings.conditions.excellentCondition');
              default: return t('recentListings.conditions.new');
            }
          };
          
          let photoUrl;
          if (produit.photos && produit.photos.length > 0) {
            photoUrl = produit.photos[0].photo_url;
          } else {
            const defaultImages = [
              "https://images.unsplash.com/photo-1580273916550-e323be2ae537",
              "https://images.unsplash.com/photo-1555212697-194d92e88b52",
              "https://images.unsplash.com/photo-1505740420928-5e560c06d30e",
              "https://images.unsplash.com/photo-1552519507-da3b142c6e3d",
              "https://images.unsplash.com/photo-1558642456-5f7ba8ac23ab",
            ];
            const randomIndex = Math.floor(Math.random() * defaultImages.length);
            photoUrl = defaultImages[randomIndex];
          }
          
          const seller = produit.user || {};
          const sellerName = seller.nom || `${t('recentListings.seller')} ${produit.userId}`;
          const sellerAvatar = getSellerAvatar(seller);
          
          const isPremium = produit.garantie && produit.garantie.trim() !== '' && produit.prix > 500000;
          
          return {
            id: produit.id,
            title: produit.titre,
            price: produit.prix,
            formattedPrice: produit.prix.toString().toLocaleString('en-US'),
            currency: 'FCFA',
            image: photoUrl,
            condition: getCondition(produit.etat),
            conditionKey: produit.etat || 'new',
            location: `${produit.city || ''}, ${produit.region || ''}`.trim(),
            timeAgo: timeAgo,
            seller: {
              id: seller.id || produit.userId,
              name: sellerName,
              avatar: sellerAvatar,
              rating: seller.isVerified ? 4.5 + (seller.id % 5) / 10 : 4.0 + (seller.id % 4) / 10,
              verified: seller.isVerified || false,
              isPro: seller.isPro || false,
              phone: seller.telephone || null,
              email: seller.email || null
            },
            isPremium: isPremium,
            isNew: diffHours < 24,
            negociable: produit.negociable === 'true' || produit.negociable === true,
            echange: produit.echange === 'true' || produit.echange === true,
            garantie: produit.garantie || '',
            description: produit.description || '',
            addressDetail: produit.adresse_detail || '',
            apiData: produit,
            createdAt: new Date(produit.createdAt)
          };
        });
        
        transformedListings.sort((a, b) => {
          return new Date(b.apiData.createdAt) - new Date(a.apiData.createdAt);
        });
        
        setRecentListings(transformedListings);
      } else {
        throw new Error('Invalid API response format');
      }
    } catch (error) {
      console.error('Error fetching recent listings:', error);
      setError(t('recentListings.errorLoading'));
      
      setRecentListings(getStaticListings());
    } finally {
      setLoading(false);
    }
  };

  const getStaticListings = () => {
    const staticListings = [
      {
        id: 1,
        title: i18n.language === 'fr' 
          ? 'Boîte de vitesses automatique Honda Accord' 
          : 'Honda Accord Automatic Transmission',
        price: 450000,
        formattedPrice: '450,000',
        currency: 'FCFA',
        image: "https://images.unsplash.com/photo-1580273916550-e323be2ae537",
        condition: t('recentListings.conditions.used'),
        conditionKey: 'used',
        location: 'Yaoundé, Mfoundi',
        timeAgo: t('recentListings.hoursAgo', { hours: 2 }),
        seller: {
          id: 1,
          name: 'Transmission Expert',
          avatar: getDefaultAvatar('Transmission Expert'),
          rating: 4.9,
          verified: true,
          isPro: true
        },
        isPremium: true,
        isNew: true,
        negociable: true,
        echange: false,
        createdAt: new Date()
      },
      {
        id: 2,
        title: i18n.language === 'fr' 
          ? 'Moteur BMW N47 2.0 Diesel' 
          : 'BMW N47 2.0 Diesel Engine',
        price: 850000,
        formattedPrice: '850,000',
        currency: 'FCFA',
        image: "https://images.unsplash.com/photo-1555212697-194d92e88b52",
        condition: t('recentListings.conditions.likeNew'),
        conditionKey: 'like_new',
        location: 'Douala, Bonabéri',
        timeAgo: t('recentListings.daysAgo', { days: 1 }),
        seller: {
          id: 2,
          name: 'BMW Parts Specialists',
          avatar: getDefaultAvatar('BMW Parts'),
          rating: 4.7,
          verified: true,
          isPro: true
        },
        isPremium: true,
        isNew: true,
        negociable: false,
        echange: true,
        createdAt: new Date(Date.now() - 86400000)
      },
      {
        id: 3,
        title: i18n.language === 'fr' 
          ? 'Alternateur Toyota RAV4 2015' 
          : 'Toyota RAV4 2015 Alternator',
        price: 120000,
        formattedPrice: '120,000',
        currency: 'FCFA',
        image: "https://images.unsplash.com/photo-1552519507-da3b142c6e3d",
        condition: t('recentListings.conditions.new'),
        conditionKey: 'new',
        location: 'Yaoundé, Nkolbisson',
        timeAgo: t('recentListings.hoursAgo', { hours: 5 }),
        seller: {
          id: 3,
          name: 'Toyota Parts Direct',
          avatar: getDefaultAvatar('Toyota Direct'),
          rating: 4.8,
          verified: false,
          isPro: false
        },
        isPremium: false,
        isNew: true,
        negociable: true,
        echange: false,
        createdAt: new Date(Date.now() - 18000000)
      },
      {
        id: 4,
        title: i18n.language === 'fr' 
          ? 'Radiateur Mercedes Classe C 220' 
          : 'Mercedes C-Class 220 Radiator',
        price: 95000,
        formattedPrice: '95,000',
        currency: 'FCFA',
        image: "https://images.unsplash.com/photo-1505740420928-5e560c06d30e",
        condition: t('recentListings.conditions.used'),
        conditionKey: 'used',
        location: 'Douala, Akwa',
        timeAgo: t('recentListings.daysAgo', { days: 3 }),
        seller: {
          id: 4,
          name: 'Euro Parts Cameroon',
          avatar: getDefaultAvatar('Euro Parts'),
          rating: 4.3,
          verified: true,
          isPro: true
        },
        isPremium: false,
        isNew: false,
        negociable: true,
        echange: true,
        createdAt: new Date(Date.now() - 259200000)
      }
    ];
    
    return staticListings;
  };

  const formatPrice = (price) => {
    const priceNum = typeof price === 'number' ? price : parseInt(price.replace(/,/g, ''));
    if (isNaN(priceNum)) return '0';
    
    return priceNum.toLocaleString('fr-CM', {
      minimumFractionDigits: 0
    });
  };

  const handleListingClick = (listing) => {
    navigate('/product', { 
      state: { 
        productId: listing.id,
        productData: listing
      }
    });
  };

  const handleSellerClick = (seller, e) => {
    e.stopPropagation();
    navigate('/seller-profile', { 
      state: { 
        userId: seller.id,
        userName: seller.name 
      }
    });
  };

  const handleViewAll = () => {
    navigate('/shop', { state: { sort: 'recent' } });
  };

  const handleLoadMore = () => {
    setDisplayCount(prev => prev + 16); // Ajoute des multiples de 4
  };

  // Fonction de tri
  const sortListings = (listings) => {
    const sorted = [...listings];
    
    switch(sortBy) {
      case 'recent':
        return sorted.sort((a, b) => b.createdAt - a.createdAt);
      case 'price-low':
        return sorted.sort((a, b) => a.price - b.price);
      case 'price-high':
        return sorted.sort((a, b) => b.price - a.price);
      case 'rating':
        return sorted.sort((a, b) => b.seller.rating - a.seller.rating);
      default:
        return sorted;
    }
  };

  // Fonction de filtrage
  const filterListings = (listings) => {
    if (filterCondition === 'all') return listings;
    
    return listings.filter(item => {
      const condition = item.conditionKey?.toLowerCase() || '';
      switch(filterCondition) {
        case 'new':
          return condition.includes('neuf') || condition.includes('new');
        case 'used':
          return condition.includes('occasion') || condition.includes('used');
        case 'like_new':
          return condition.includes('comme neuf') || condition.includes('like_new');
        default:
          return true;
      }
    });
  };

  // Appliquer tri et filtrage
  const processedListings = filterListings(sortListings(recentListings));
  const displayedListings = processedListings.slice(0, displayCount);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[600px] bg-gradient-to-b from-slate-50 to-white">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mb-6"></div>
          <p className="text-xl text-slate-700 font-medium mb-2">
            {t('recentListings.loading')}
          </p>
          <p className="text-slate-500">
            {t('recentListings.loadingSubtitle')}
          </p>
        </div>
      </div>
    );
  }

  if (error && recentListings.length === 0) {
    return (
      <div className="flex flex-col justify-center items-center min-h-[600px] bg-gradient-to-b from-slate-50 to-white p-8">
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 max-w-lg p-8">
          <div className="flex flex-col items-center text-center">
            <div className="bg-red-50 p-4 rounded-full mb-6">
              <Icon name="AlertCircle" size={48} color="#ef4444" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-3">
              {t('recentListings.errorTitle')}
            </h3>
            <p className="text-slate-600 mb-8">{error}</p>
            <div className="flex flex-col sm:flex-row gap-4 w-full">
              <button
                onClick={fetchRecentListings}
                className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-md hover:shadow-lg"
              >
                {t('recentListings.retry')}
              </button>
              <button
                onClick={() => navigate('/')}
                className="flex-1 border-2 border-slate-300 hover:border-slate-400 text-slate-700 hover:text-slate-800 px-6 py-3 rounded-xl font-semibold transition-all duration-300"
              >
                {t('recentListings.goHome')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-b from-slate-50 to-white py-10 px-4 md:px-6 lg:px-8">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-10">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-blue-100 p-2 rounded-lg">
                <Icon name="Clock" size={24} color="#3b82f6" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
                {t('recentListings.sectionTitle')}
              </h2>
            </div>
            <p className="text-lg text-slate-600 max-w-3xl">
              {t('recentListings.sectionSubtitle')}
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => navigate('/create-listing', { state: { isEditMode: false } })}
              className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white px-5 py-3 rounded-xl font-semibold transition-all duration-300 shadow-md hover:shadow-lg inline-flex items-center justify-center whitespace-nowrap"
            >
              <Icon name="Plus" size={18} color="white" className="mr-2" />
              <span>{t('recentListings.addListing')}</span>
            </button>
            
            <button
              onClick={handleViewAll}
              className="border-2 border-orange-500 hover:border-orange-600 text-orange-500 hover:text-orange-600 px-5 py-3 rounded-xl font-semibold transition-all duration-300 inline-flex items-center justify-center whitespace-nowrap"
            >
              <span>{t('recentListings.viewAll')}</span>
              <Icon name="ArrowRight" size={18} color="currentColor" className="ml-2" />
            </button>
          </div>
        </div>
      </div>

      {/* Listings Grid - 4 par ligne */}
      {recentListings.length === 0 ? (
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl shadow-sm border border-slate-200">
            <div className="bg-slate-50 p-6 rounded-full mb-6">
              <Icon name="Package" size={80} color="#cbd5e1" />
            </div>
            <h3 className="text-2xl font-bold text-slate-800 mb-3">
              {t('recentListings.noListingsTitle')}
            </h3>
            <p className="text-slate-600 text-center mb-8 max-w-md">
              {t('recentListings.noListings')}
            </p>
            <button
              onClick={() => navigate('/create-listing', { state: { isEditMode: false } })}
              className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white px-8 py-4 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              {t('recentListings.addListing')}
            </button>
          </div>
        </div>
      ) : (
        <div className="max-w-7xl mx-auto">
          {/* Compteur */}
          <div className="mb-6 flex items-center justify-between">
            <p className="text-slate-700">
              <span className="font-bold text-blue-600">{displayedListings.length}</span> 
              <span className="mx-2">sur</span>
              <span className="font-bold text-slate-900">{processedListings.length}</span> 
              <span className="ml-2">annonces</span>
            </p>
            {processedListings.length > 0 && (
              <button
                onClick={fetchRecentListings}
                className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium transition-colors"
              >
                <Icon name="RefreshCw" size={18} color="currentColor" className="mr-2" />
                {t('recentListings.refresh')}
              </button>
            )}
          </div>

          {/* Grille 4 par ligne */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {displayedListings.map((item, index) => (
              <div
                key={item.id}
                className={`bg-white rounded-xl border overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 group ${
                  item.isPremium ? 'border-amber-300 shadow-amber-50' : 'border-slate-200'
                }`}
                style={{
                  animationDelay: `${index * 50}ms`
                }}
              >
                <div 
                  className="cursor-pointer h-full flex flex-col"
                  onClick={() => handleListingClick(item)}
                >
                  {/* Image Container */}
                  <div className="relative h-48 overflow-hidden">
                    {/* Badges */}
                    <div className="absolute top-3 left-3 z-10 flex flex-col gap-1">
                      {item.isPremium && (
                        <div className="flex items-center bg-gradient-to-r from-amber-500 to-amber-600 px-2 py-1.5 rounded-md shadow-md">
                          <Icon name="Star" size={12} color="white" />
                          <span className="ml-1.5 text-xs font-bold text-white">PREMIUM</span>
                        </div>
                      )}
                      {item.isNew && (
                        <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 px-2 py-1.5 rounded-md shadow-md">
                          <span className="text-xs font-bold text-white">NOUVEAU</span>
                        </div>
                      )}
                    </div>

                    {/* Image */}
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onLoadStart={() => setLoadingImages(prev => ({...prev, [item.id]: true}))}
                      onLoad={() => setLoadingImages(prev => ({...prev, [item.id]: false}))}
                      onError={(e) => {
                        setLoadingImages(prev => ({...prev, [item.id]: false}));
                        e.target.src = "https://musee-possen.lu/wp-content/uploads/2020/08/placeholder.png";
                      }}
                    />
                    
                    {/* Time Badge */}
                    <div className="absolute bottom-3 right-3 flex items-center bg-white/95 backdrop-blur-sm px-2.5 py-1.5 rounded-full shadow border border-slate-200">
                      <Icon name="Clock" size={12} color="#1e293b" />
                      <span className="ml-1.5 text-xs font-semibold text-slate-800">
                        {item.timeAgo}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-4 flex-1 flex flex-col">
                    <h3 className="font-bold text-slate-900 mb-3 line-clamp-2 h-12 text-sm leading-tight group-hover:text-blue-600 transition-colors">
                      {item.title}
                    </h3>
                    
                    <div className="mb-3">
                      <p className="text-xl font-bold text-blue-700 mb-2">
                        {formatPrice(item.price)} {item.currency}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="bg-slate-100 text-slate-700 px-3 py-1 rounded-full text-xs font-medium">
                          {item.condition}
                        </span>
                        <div className="flex items-center gap-1">
                          {item.negociable && (
                            <span className="text-xs text-emerald-600 font-medium">
                              Négociable
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center text-slate-600 mb-4">
                      <Icon name="MapPin" size={14} color="#64748b" className="flex-shrink-0" />
                      <span className="ml-2 text-sm truncate">
                        {item.location}
                      </span>
                    </div>
                    
                    {/* Seller Info */}
                    <div 
                      className="mt-auto pt-4 border-t border-slate-100 cursor-pointer hover:bg-slate-50 p-2 -mx-2 rounded-lg transition-colors"
                      onClick={(e) => handleSellerClick(item.seller, e)}
                    >
                      <div className="flex items-center">
                        <div className="relative">
                          <img
                            src={item.seller.avatar}
                            alt={item.seller.name}
                            className="w-9 h-9 rounded-full border-2 border-white shadow"
                            onError={(e) => {
                              e.target.src = getDefaultAvatar(item.seller.name);
                            }}
                          />
                          {item.seller.isPro && (
                            <div className="absolute -bottom-1 -right-1 bg-blue-500 text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center border border-white">
                              <Icon name="Check" size={8} color="white" />
                            </div>
                          )}
                        </div>
                        <div className="ml-3 flex-1 min-w-0">
                          <div className="flex items-center">
                            <span className="text-sm font-semibold text-slate-800 truncate">
                              {item.seller.name}
                            </span>
                            {item.seller.verified && (
                              <Icon name="ShieldCheck" size={12} color="#10b981" className="ml-1.5 flex-shrink-0" />
                            )}
                          </div>
                          <div className="flex items-center mt-0.5">
                            <div className="flex">
                              {[...Array(5)].map((_, i) => (
                                <Icon 
                                  key={i} 
                                  name="Star" 
                                  size={10} 
                                  color={i < Math.floor(item.seller.rating) ? "#f59e0b" : "#cbd5e1"} 
                                  className="mr-0.5"
                                />
                              ))}
                            </div>
                            <span className="ml-1.5 text-xs font-semibold text-slate-800">
                              {item.seller.rating.toFixed(1)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Additional Info */}
                    <div className="mt-3 flex flex-wrap gap-1">
                      {item.echange && (
                        <div className="bg-amber-50 text-amber-700 px-2 py-1 rounded text-xs font-semibold inline-flex items-center">
                          <Icon name="RefreshCw" size={10} color="#f59e0b" className="mr-1.5" />
                          Échange
                        </div>
                      )}
                      {item.garantie && (
                        <div className="flex items-center bg-emerald-50 text-emerald-800 px-2 py-1 rounded text-xs font-semibold">
                          <Icon name="ShieldCheck" size={10} color="#10b981" className="mr-1.5" />
                          {item.garantie}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Load More Button */}
          {displayCount < processedListings.length && (
            <div className="text-center mt-10">
              <button
                onClick={handleLoadMore}
                className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-8 py-3.5 rounded-xl font-bold transition-all duration-300 shadow-lg hover:shadow-xl inline-flex items-center"
              >
                <span>Charger plus ({processedListings.length - displayCount} restants)</span>
                <Icon name="ChevronDown" size={20} color="white" className="ml-3" />
              </button>
              <p className="mt-3 text-slate-500 text-sm">
                {displayedListings.length} sur {processedListings.length} annonces affichées
              </p>
            </div>
          )}
        </div>
      )}

    

      <style jsx>{`
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        
        .grid > div {
          animation: fadeInUp 0.3s ease-out forwards;
          opacity: 0;
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        /* Assurer une hauteur cohérente pour toutes les cartes */
        .grid > div {
          min-height: 420px;
        }
      `}</style>
    </div>
  );
};

export default RecentListingsGrid;